import React from "react";
import { Node, Editor } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import _ from "lodash";
import createSuggestion from "@/components/mention/suggestion";

interface QueryBuilderInputProps {
  onSearch: (query: string, queryBy?: string) => void;
  defaultValue?: string;
  suggestions: { id: string; label: string }[];
}

const QueryBuilderInput: React.FC<QueryBuilderInputProps> = ({
  onSearch,
  defaultValue,
  suggestions,
}) => {
  const formatText = ({ editor, text }: { editor: Editor; text?: string }) => {
    const query = text || editor.getText();
    const [entityType, q] = query?.split(":") ?? [];

    if (editor && q?.length >= 0) {
      editor.commands.setContent(`<mark>${entityType}:</mark>${q}`, false, {
        preserveWhitespace: true,
      });
    } else {
      editor.commands.setContent(query, false, { preserveWhitespace: true });
    }
  };

  const editor = useEditor({
    extensions: [
      OneLiner,
      Placeholder.configure({
        placeholder: "Type / to search",
      }),
      Text,
      Paragraph,
      Highlight.configure({
        HTMLAttributes: {
          style:
            "background-color: #388bfd1a; color: #4493f8; border-radius:0.1875rem;",
        },
      }),
      Mention.configure({
        HTMLAttributes: {
          class: "mention",
        },
        suggestion: createSuggestion(suggestions),
      }),
    ],
    content: defaultValue || "",
    editorProps: {
      attributes: {
        class:
          "flex h-10 w-full rounded-md border border-input bg-muted/90 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
      },
    },
    onCreate: formatText,
    onUpdate: formatText,
  });

  React.useEffect(() => {
    const focusQueryBuilder = (e: KeyboardEvent) => {
      if (e.key === "/" && !editor?.isFocused) {
        e.preventDefault();
        editor?.commands.focus();
      }
    };

    document.addEventListener("keydown", focusQueryBuilder);

    return () => {
      document.removeEventListener("keydown", focusQueryBuilder);
    };
  }, []);

  React.useEffect(() => {
    if (editor) formatText({ editor, text: defaultValue });
  }, [defaultValue]);

  return (
    // <div className="relative border rounded-md p-2 bg-background min-w-[400px]">
    <div className="flex flex-col w-[150px] lg:w-[400px]">
      <EditorContent
        editor={editor}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            const editorJson = editor?.getText();
            if (editorJson?.includes(":")) {
              const [entityType, q] = editorJson?.split(":") ?? [];
              onSearch(q, entityType.replace(":", ""));
            } else {
              onSearch(editorJson ?? "");
            }
            return false;
          }
        }}
      />
    </div>
  );
};

const OneLiner = Node.create({
  name: "oneLiner",
  topNode: true,
  content: "block",
});

export default QueryBuilderInput;
